// @ts-check
/// <reference path="../../types/index.js" />

/**
 * A function that handles closing actions with a specific reason.
 * @callback HandleClose
 * @param {{}} event - The event that triggers the close.
 * @param {"backdropClick" | "escapeKeyDown"} reason - The reason for closing.
 * @returns {void}
 */

import Search from './Search';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Grid from '@mui/material/Grid';
import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import hexToRGBA from '../../util/HEXToRGBA';
import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import StarIcon from '@material-ui/icons/Star';
import InfoIcon from '@material-ui/icons/Info';
import Typography from '@mui/material/Typography';
import starImage from '../../assets/images/star.svg';
import globalColors from '../../theme/GlobalColorsv2';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useSelector, useDispatch } from 'react-redux';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import ListItemButton from '@mui/material/ListItemButton';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import React, { useEffect, useContext, useState, useMemo } from 'react';
import SelectTabContextProvider from '../SelectTabContext/SelectTabContext';
import useAuditLogs from '../../hooks/useAuditLogs';
import {
  postFavouriteWindows,
  fetchFavouriteWindows,
  resetFavouriteWindowsStatus,
  SuccessMsg as successMessage,
} from '../../redux/reducers/FavouriteWindows';

const CustomListItem = styled(ListItemButton)(({ theme }) => ({
  fontSize: '14px !important',
  '&:hover': {
    backgroundColor: hexToRGBA('#E3F2FF', '.65'),
  },
  '&:hover .hoverIcons': {
    visibility: 'visible',
  },
  '.hoverIcons': {
    visibility: 'hidden',
  },
}));

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // Correctly access theme here
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 220,
    color: theme.palette.common.white, // Corrected 'common' typo from 'comming'
    backgroundColor: theme.palette.info.dark,
    fontSize: '14px',
    padding: '20px',
  },
}));

/**
 * Displays content inside a popover.
 * @param {Object} props - The properties object.
 * @param {boolean} props.open - Indicates whether the Content Box is opened or closed.
 * @param {Element|null} props.anchorEl - The name of the anchor element.
 * @param {HandleClose} props.handleClose - Function to call when closing the Content Box.
 * @return {React.JSX.Element} - React Component
 */
const ContentBox = ({ open, anchorEl, handleClose }) => {
  /**
   * The current value of the search input field.
   * @type {[string, React.Dispatch<React.SetStateAction<string>>]}
   */
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * State for holding an array of window objects, initially empty.
   * Uses JSDoc to hint expected type information for better developer understanding
   * and tooling support in JS files.
   * @type {[WindowData[], Function]}
   */
  const [filteredWindows, setFilteredWindows] = useState([]);

  /**
   * Retrieves role data from the Redux store.
   * @return {RoleData} The role data.
   */
  const dispatch = useDispatch();

  /**
   * Selects role data from the Redux state.
   * @param {ReduxState} state - The Redux state.
   * @return {RoleData} - The role data.
   */
  const selectRoleData = ({ role }) => role;
  const { roleData } = useSelector(selectRoleData);

  /**
   * Retrieves UD Window data from the Redux store.
   * @param {ReduxState} state - The Redux state.
   * @return {UdwData} The UD Window data.
   */
  const selectUDWData = ({ udwWindows }) => udwWindows;
  const { udwData } = useSelector(selectUDWData);

  /**
   * Retrieves UD Window data from the Redux store.
   * @param {ReduxState} state - The Redux state.
   * @return {FavouriteWindowsData} The Favorite Window data.
   */
  const selectfavouriteWindowsData = ({ favouriteWindows }) => favouriteWindows;
  const { favouriteWindowsData, favouriteWindowsStatus } = useSelector(selectfavouriteWindowsData);

  /**
   *
   * Effects
   *
   */

  // Fetch featured windows from the server.
  useEffect(() => {
    if (roleData?.userDetails?.userName) {
      dispatch(fetchFavouriteWindows(roleData?.userDetails?.userName));
    }
  }, [roleData]);
  const { windowsData, selectedTabs, defaultWindows, setSelectedTabs } =
    useContext(SelectTabContextProvider);

  // Builds Featured windows from the list of windows
  // using the fetched featured codes.
  useEffect(() => {
    const filteredCoreWindows = windowsData.filter((window) =>
      favouriteWindowsData?.windowName?.includes(window.code),
    );

    /**
     * Transforms CustomWindowData into WindowData.
     * @param {CustomWindowData} window An individual custom window data item.
     * @return {WindowData} The transformed window data.
     */
    const transformToWindowData = (window) => ({
      ...window,
      code: window.windowName,
      label: window.windowDescription,
    });

    /**
     * Checks if a window is in the list of favourite windows.
     * @param {WindowData} window An individual window data item.
     * @return {boolean} True if the window is a favourite, false otherwise.
     */
    const isFavouriteWindow = (window) => favouriteWindowsData?.windowName?.includes(window.code);

    const filteredCustomWindows = udwData.map(transformToWindowData).filter(isFavouriteWindow);

    setFilteredWindows([...filteredCoreWindows, ...filteredCustomWindows]);
  }, [windowsData, favouriteWindowsData, udwData]);

  /**
   * Handles success message when adding featured windows.
   * Resets and Fetches Featured windows using server data
   * to make sure data matches.
   */
  useEffect(() => {
    if (favouriteWindowsStatus === 200) {
      setTimeout(() => {
        dispatch(successMessage());
      }, 3000);
      dispatch(resetFavouriteWindowsStatus());
      dispatch(fetchFavouriteWindows(roleData?.userDetails?.userName));
    }
  }, [favouriteWindowsStatus]);

  /**
   *
   * Functions
   *
   */

  /**
   * Adds a certain code from {WindowData} to favorites on the backend.
   * @param {string} code - Is an identifier for each Window.
   * @param {React.MouseEvent} event - The event object to stop propagation.
   */
  const handleAddToFavorite = (code, event) => {
    event.stopPropagation();

    let favoriteWindows = [...favouriteWindowsData.windowName];
    if (favoriteWindows.includes(code)) {
      favoriteWindows = favoriteWindows.filter((item) => item !== code);
    } else {
      favoriteWindows.push(code);
    }

    dispatch(
      postFavouriteWindows({
        windowName: favoriteWindows.length > 0 ? [...favoriteWindows] : [],
      }),
    );
  };

  /**
   * Toggles the selection status of a window based on its code. If the window is currently
   * selected, it will be removed from the selection; if it is not selected, it will be added.
   * The function updates the `selectedTabs` state with the new selection list.
   *
   * @param {string} code - The unique identifier for a window. This code is used to determine
   *                        whether the window is already selected.
   */
  const handleToggleCheckbox = (code) => {
    // Check if the code is already in the selected tabs
    const currentIndex = selectedTabs.findIndex((tab) => tab.code === code);
    const newSelectedTabs = [...selectedTabs];

    if (currentIndex === -1) {
      const foundWindow =
        windowsData.find((window) => {
          return window.code === code;
        }) ||
        udwData.find((window) => {
          return window.windowName === code;
        });

      if (!foundWindow) {
        // If no window is found, log an error or handle it appropriately
        console.error('No window found with the given code:', code);
        return;
      }

      newSelectedTabs.push({
        code: foundWindow.code || foundWindow.windowName,
        label: foundWindow.label || foundWindow.windowDescription,
      });

      setSelectedTabs(newSelectedTabs);
      return;
    }

    newSelectedTabs.splice(currentIndex, 1);
    setSelectedTabs(newSelectedTabs);
  };

  // useMemo to recalculate the filtered arrays only when searchTerm changes
  const filteredData = useMemo(() => {
    /**
     * Checks if a window is in the list of favourite windows.
     * @param {WindowData} item An individual window data item.
     * @return {boolean} True if the window is a favourite, false otherwise.
     */
    const isStringPresent = (item) => item?.label?.toLowerCase().includes(searchTerm.toLowerCase());

    /**
     * Checks if a window is in the list of favourite windows.
     * @param {WindowData[]} array An individual window data item.
     * @return {WindowData[]} filterArray - Filtered arrays by search term
     */
    const filterArray = (array) => array.filter(isStringPresent);

    /**
     * Checks if a window is in the list of favourite windows.
     * @param {CustomWindowData} item An individual window data item.
     * @return {boolean} True if the window is a favourite, false otherwise.
     */
    const isStringPresentInCustomData = (item) => {
      return item?.windowDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    };

    /**
     * Checks if a window is in the list of favourite windows.
     * @param {CustomWindowData[]} array An individual window data item.
     * @return {CustomWindowData[]} filterArray - Filtered arrays by search term
     */
    const filterCustomArray = (array) => array.filter(isStringPresentInCustomData);

    return {
      core: filterArray(windowsData),
      default: filterArray(defaultWindows),
      custom: filterCustomArray(udwData),
    };
  }, [searchTerm, filteredWindows, defaultWindows, udwData]);

  /**
   * Handles Audit Log data for each window.
   **/
  const dataText = `Available Audit Log`;
  const { auditLogs } = useAuditLogs();
  const [WindowsWithData, setWindowsWithData] = useState([]);
  useEffect(() => {
    if (auditLogs && auditLogs.length > 0) {
      const windows = auditLogs.map((item) => item.field);
      setWindowsWithData(windows);
    }
  }, [auditLogs]);

  /**
   * Handles a checked checkbox in a custom window list element
   * @param {string} term - Is an identifier for each Window.
   */
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const defaultText = `Default windows are assigned and managed by admin. These 
  windows will always show as selected, but the visibility can be toggled on the chip.`;

  const coreWindowText = (
    <React.Fragment>
      <Typography
        variant="body2"
        sx={{
          color: globalColors.white,
        }}
      >
        When windows are selected, this creates a chip.
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 1,
          color: globalColors.white,
        }}
      >
        Chips allow window visibility to be toggled, or deselected by selecting the close ‘x’ icon.
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 1,
          color: globalColors.white,
        }}
      >
        Window names in{' '}
        <strong>
          <i>Bold Italics</i>
        </strong>
        , indicate a window with data.
      </Typography>
    </React.Fragment>
  );

  return (
    <Popover
      anchorEl={anchorEl}
      onClose={handleClose}
      open={open}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        width: 'auto',
        height: 'auto',
      }}
    >
      <Grid
        container
        sx={{
          width: '560px',
          padding: '20px 0',
        }}
      >
        {/* Column 1 */}
        <Grid
          item
          xs={6}
          sx={{
            maxHeight: 472,
            overflow: 'auto',
            borderRight: 1,
            borderColor: 'grey.300',
          }}
        >
          <Typography component="h5" fontWeight="bold" sx={{ padding: '0 15px' }}>
            All Windows
          </Typography>

          <Search onSearch={handleSearch} />

          {/* Default Windows */}
          <Box>
            <Typography
              component="h6"
              fontWeight="bold"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 15px',
                marginTop: '20px',
                fontWeight: 'bold',
              }}
            >
              Default Windows{' '}
              <CustomWidthTooltip title={defaultText}>
                <InfoIcon
                  style={{
                    height: '16px',
                    width: '16px',
                    color: globalColors.palette.info.dark,
                  }}
                />
              </CustomWidthTooltip>
            </Typography>
            <List component="nav" aria-label="main mailbox folders">
              {/* Repeat ListItem and ListItemText for each item */}
              {filteredData.default.map((window, index) => (
                <CustomListItem key={index}>
                  <ListItemIcon
                    sx={{
                      minWidth: '20px',
                    }}
                  >
                    <BookmarkIcon
                      style={{
                        color: globalColors.palette.info.dark,
                        marginRight: '10px',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: selectedTabs.some(item => item.code === window.code) ? '800' : '400',
                        fontStyle: selectedTabs.some(item => item.code === window.code) ? 'italic' : 'normal'
                      },
                    }}
                    primary={window.label}
                  />
                </CustomListItem>
              ))}
            </List>
          </Box>
          <Divider
            sx={{
              backgroundColor: globalColors.dividerColor,
            }}
          />

          {/* Core Windows */}
          <Box>
            <Typography
              component="h6"
              fontWeight="bold"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 15px',
                marginTop: '20px',
                fontWeight: 'bold',
              }}
            >
              Core Windows{' '}
              <CustomWidthTooltip title={coreWindowText}>
                <InfoIcon
                  style={{
                    height: '16px',
                    width: '16px',
                    color: globalColors.palette.info.dark,
                  }}
                />
              </CustomWidthTooltip>
            </Typography>
            <List component="nav">
              {filteredData.core.map((window, index) => (
                <CustomListItem
                  key={index}
                  onClick={() => handleToggleCheckbox(window.code)}
                  sx={{
                    ...(selectedTabs.some((item) => item.code === window.code) && {
                      backgroundColor: globalColors.palette.info.lighter,
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: '20px',
                    }}
                  >
                    <Checkbox
                      edge="start"
                      tabIndex={-1}
                      disableRipple
                      checked={selectedTabs.some((item) => item.code === window.code)}
                      sx={{
                        marginLeft: '0',
                        marginRight: '10px',
                        padding: 0,
                      }}
                      name={window.code}
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: selectedTabs.some(item => item.code === window.code) ? '800' : '400',
                        fontStyle: selectedTabs.some(item => item.code === window.code) ? 'italic' : 'normal'
                      },
                    }}
                    primary={window.label}
                  />
                  <Box sx={{ display: 'flex' }}>
                    {favouriteWindowsData?.windowName?.includes(window.code) ? (
                      <StarIcon
                        onClick={(e) => handleAddToFavorite(window.code, e)}
                        style={{
                          color: globalColors.palette.info.main,
                        }}
                      /> // Is the current window tagged as favorite
                    ) : (
                      <StarBorderIcon
                        className="hoverIcons"
                        onClick={(e) => handleAddToFavorite(window.code, e)}
                        style={{
                          color: globalColors.palette.info.main,
                        }}
                      />
                    )}
                  </Box>
                </CustomListItem>
              ))}
            </List>
          </Box>
          <Divider
            sx={{
              backgroundColor: globalColors.dividerColor,
            }}
          />

          {/* Custom Windows */}
          <Box>
            <Typography
              component="h6"
              fontWeight="bold"
              sx={{ padding: '0 15px', marginTop: '20px' }}
            >
              Custom Windows
            </Typography>
            <List component="nav">
              {filteredData.custom.map((window, index) => (
                <CustomListItem
                  key={index}
                  onClick={() => handleToggleCheckbox(window.windowName)}
                  sx={{
                    ...(selectedTabs.some((item) => item.code === window.windowName) && {
                      backgroundColor: globalColors.palette.info.lighter,
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: '20px',
                    }}
                  >
                    <Checkbox
                      edge="start"
                      tabIndex={-1}
                      disableRipple
                      checked={selectedTabs.some((item) => item.code === window.windowName)}
                      sx={{
                        marginLeft: '0',
                        marginRight: '10px',
                        padding: 0,
                      }}
                      name={window.windowName}
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: (WindowsWithData.includes(window.windowDescription) || 
                                    selectedTabs.some(item => item.code === window.windowName)) 
                          ? '800' 
                          : '400',
                        fontStyle: (WindowsWithData.includes(window.windowDescription) || 
                                  selectedTabs.some(item => item.code === window.windowName))
                          ? 'italic'
                          : 'normal',
                      },
                    }}
                    primary={window.windowDescription}
                  />
                  {WindowsWithData.includes(window.windowDescription) && (
                    <CustomWidthTooltip title={dataText}>
                      <InfoIcon
                        style={{
                          height: '16px',
                          width: '16px',
                          color: globalColors.palette.info.dark,
                        }}
                      />
                    </CustomWidthTooltip>
                  )}

                  <Box sx={{ display: 'flex' }}>
                    {favouriteWindowsData?.windowName?.includes(window.windowName) ? (
                      <StarIcon
                        onClick={(e) => handleAddToFavorite(window.windowName, e)}
                        style={{
                          color: globalColors.palette.info.main,
                        }}
                      />
                    ) : (
                      <StarBorderIcon
                        className="hoverIcons"
                        onClick={(e) => handleAddToFavorite(window.windowName, e)}
                        style={{
                          color: globalColors.palette.info.main,
                        }}
                      />
                    )}
                  </Box>
                </CustomListItem>
              ))}
            </List>
          </Box>
        </Grid>

        {/* Column 2 for Favorites */}
        <Grid
          item
          xs={6}
          sx={{
            maxHeight: 472,
            overflow: 'auto',
          }}
        >
          <Typography
            component="h5"
            fontWeight="bold"
            sx={{
              marginBottom: '20px',
              marginLeft: '20px',
            }}
          >
            Favorites
          </Typography>
          {filteredWindows.length > 0 ? (
            filteredWindows.map((window, index) => (
              <CustomListItem
                key={index}
                onClick={() => handleToggleCheckbox(window.code)}
                sx={{
                  ...(selectedTabs.some((item) => item.code === window.code) && {
                    backgroundColor: globalColors.palette.info.lighter,
                  }),
                }}
              >
                <ListItemIcon
                  sx={{
                    marginRight: '-20px',
                  }}
                >
                  <StarIcon
                    onClick={(e) => handleAddToFavorite(window.code, e)}
                    style={{
                      color: globalColors.palette.info.main,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedTabs.some(item => item.code === window.code) ? '800' : '400',
                      fontStyle: selectedTabs.some(item => item.code === window.code) ? 'italic' : 'normal'
                    },
                  }}
                  primary={window.label}
                />
              </CustomListItem>
            ))
          ) : (
            <Box
              sx={{
                textAlign: 'center',
              }}
            >
              <img src={starImage} alt="Add to Favorite" />
              <Typography variant="body1" component="h2" fontWeight="bold">
                Add a Favorite
              </Typography>
              <Typography variant="body1" sx={{ padding: '20px' }}>
                When hovering on a window name in the list, select the star icon to add as a
                favorite
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Popover>
  );
};

export default ContentBox;
